import { CmsAiMarkerPosition, CmsAiMarkerStyle } from '@spa/generated/graphql/graphql';
import { ReactElement, memo } from 'react';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import styles from './image-ai-marker.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';

export interface Props {
  readonly style?: CmsAiMarkerStyle;
  readonly show?: boolean;
  readonly position?: CmsAiMarkerPosition;
  readonly alt?: string;
  readonly className?: string;
}

const POSITION_CLASSES: Record<CmsAiMarkerPosition, string> = {
  [CmsAiMarkerPosition.BOTTOM_RIGHT]: styles['image-ai-marker--bottom-right'],
  [CmsAiMarkerPosition.BOTTOM_LEFT]: styles['image-ai-marker--bottom-left'],
  [CmsAiMarkerPosition.TOP_RIGHT]: styles['image-ai-marker--top-right'],
  [CmsAiMarkerPosition.TOP_LEFT]: styles['image-ai-marker--top-left']
};

const STYLE_CLASSES: Record<CmsAiMarkerStyle, string> = {
  [CmsAiMarkerStyle.BLACK_ON_WHITE]: styles['image-ai-marker--black-on-white'],
  [CmsAiMarkerStyle.WHITE_ON_BLACK]: styles['image-ai-marker--white-on-black']
};

export const ImageAiMarkerComponent = memo(function ImageAiMarkerComponent({
  show = true,
  position = CmsAiMarkerPosition.BOTTOM_RIGHT,
  style = CmsAiMarkerStyle.BLACK_ON_WHITE,
  alt = '',
  className
}: Props): ReactElement | null {
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('image-ai-marker');

  if (!show) {
    return null;
  }

  const isDecorativeImage = alt === '';
  const disclosureText = formatMessage('accessibleLabel');

  return (
    <div
      className={classNames(
        styles['image-ai-marker'],
        POSITION_CLASSES[position],
        STYLE_CLASSES[style],
        className
      )}
      role={isDecorativeImage ? 'img' : undefined}
      aria-hidden={isDecorativeImage ? undefined : 'true'}
      aria-label={isDecorativeImage ? disclosureText : undefined}
    >
      <div className={styles['image-ai-marker__content']} aria-hidden="true">
        <span className={styles['image-ai-marker__compact']}>{formatMessage('compact')}</span>
        <span className={styles['image-ai-marker__expanded']}>{formatMessage('expanded')}</span>
      </div>
    </div>
  );
});
